import { useCallback, useEffect, useMemo, useState } from "react";
import { BOT_CHAINS, chainLabel, isBotChain } from "../wallet/chains";

function shortAddress(address) {
  if (!address) return "Not connected";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export default function WalletReadiness() {
  const [address, setAddress] = useState("");
  const [chainId, setChainId] = useState(null);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState("");
  const ethereum = typeof window !== "undefined" ? window.ethereum : null;
  const isConnected = Boolean(address);
  const supported = isConnected && isBotChain(chainId);

  const readWallet = useCallback(async () => {
    if (!ethereum?.request) return;
    const [accounts, chainHex] = await Promise.all([
      ethereum.request({ method: "eth_accounts" }),
      ethereum.request({ method: "eth_chainId" }),
    ]);
    setAddress(accounts?.[0] || "");
    setChainId(chainHex ? Number.parseInt(chainHex, 16) : null);
  }, [ethereum]);

  useEffect(() => {
    readWallet().catch(() => {});
    if (!ethereum?.on) return undefined;
    const handleAccounts = (accounts) => setAddress(accounts?.[0] || "");
    const handleChain = (chainHex) => setChainId(Number.parseInt(chainHex, 16));
    ethereum.on("accountsChanged", handleAccounts);
    ethereum.on("chainChanged", handleChain);
    return () => {
      ethereum.removeListener?.("accountsChanged", handleAccounts);
      ethereum.removeListener?.("chainChanged", handleChain);
    };
  }, [ethereum, readWallet]);

  const currentChain = useMemo(
    () => BOT_CHAINS.find((chain) => chain.id === Number(chainId)),
    [chainId],
  );

  async function connectWallet() {
    if (!ethereum?.request) {
      setMessage("Wallet not found. On desktop, install MetaMask, Rabby, OKX, or Coinbase Wallet. On mobile, open this page inside your wallet app browser.");
      return;
    }
    setPending(true);
    setMessage("");
    try {
      const accounts = await ethereum.request({ method: "eth_requestAccounts" });
      setAddress(accounts?.[0] || "");
      await readWallet();
    } catch (err) {
      setMessage(err?.message || "Wallet connection was rejected.");
    } finally {
      setPending(false);
    }
  }

  async function switchToChain(chain) {
    if (!ethereum?.request) return;
    setPending(true);
    setMessage("");
    const chainHex = `0x${chain.id.toString(16)}`;
    try {
      await ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: chainHex }],
      });
    } catch (err) {
      if (err?.code === 4902) {
        await ethereum.request({
          method: "wallet_addEthereumChain",
          params: [{
            chainId: chainHex,
            chainName: chain.name,
            nativeCurrency: chain.nativeCurrency,
            rpcUrls: chain.rpcUrls,
            blockExplorerUrls: chain.blockExplorerUrls,
          }],
        });
      } else {
        setMessage(err?.message || `Could not switch to ${chain.name}.`);
      }
    } finally {
      await readWallet().catch(() => {});
      setPending(false);
    }
  }

  function disconnectView() {
    setAddress("");
    setMessage("Wallet hidden in this dashboard. Disconnect fully inside your wallet extension.");
  }

  return (
    <section className={`wallet-readiness ${supported ? "ready" : ""}`}>
      <div className="wallet-readiness-copy">
        <span className="auto-eyebrow">Test wallet readiness</span>
        <h2>Connect a wallet for bot validation.</h2>
        <p>
          The wallet is used for manual test execution only. Test bots keep
          running from live signals, and live auto-execution remains locked.
        </p>
      </div>

      <div className="wallet-readiness-panel">
        <div className="wallet-status-grid">
          <div>
            <span>Wallet</span>
            <strong>{shortAddress(address)}</strong>
          </div>
          <div>
            <span>Network</span>
            <strong>{isConnected ? chainLabel(chainId) : "Not connected"}</strong>
          </div>
          <div>
            <span>Bot networks</span>
            <strong>{BOT_CHAINS.length}</strong>
          </div>
          <div>
            <span>Execution</span>
            <strong>Manual test only</strong>
          </div>
        </div>

        <div className="wallet-actions-row">
          {isConnected ? (
            <button type="button" className="wallet-action secondary" onClick={disconnectView}>
              Hide Wallet
            </button>
          ) : (
            <button
              type="button"
              className="wallet-action"
              disabled={pending}
              onClick={connectWallet}
            >
              {pending ? "Connecting..." : "Connect Wallet"}
            </button>
          )}
        </div>

        <div className="wallet-chain-list">
          {BOT_CHAINS.map((chain) => (
            <button
              type="button"
              className={chain.id === chainId ? "active" : ""}
              disabled={!isConnected || pending}
              key={chain.id}
              onClick={() => switchToChain(chain)}
            >
              {chain.name}
            </button>
          ))}
        </div>

        {message && <div className="wallet-readiness-message">{message}</div>}

        <div className={`wallet-readiness-note ${supported ? "ok" : "hold"}`}>
          {supported
            ? `${currentChain?.name || "This network"} is ready for manual bot validation. Token Risk is still required before any test swap.`
            : "Connect or switch to Ethereum, Base, Arbitrum, Optimism, Polygon, or BNB."}
        </div>
      </div>
    </section>
  );
}
